import type { FetchResult } from "@apollo/client";
import { ApolloLink, Observable } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

/**
 * Key the auth link stamps with the access token it actually sent — on the operation context, and
 * again on the failure itself.
 *
 * The context copy is for the error link: `operation.setContext` merges, and `onError` closes over
 * the same `operation` instance the downstream links mutated, so an error link installed *above*
 * the auth link reads what the auth link wrote.
 *
 * The copy on the failure is for everyone downstream of the link chain, `AccountProvider` above
 * all. An `ApolloError` carries no operation context, so without it the provider cannot tell which
 * credential a 401 belongs to — and it is the other sign-out authority, so suppressing
 * `onUnauthorized` alone leaves the credential to be destroyed one layer up.
 */
export const AUTH_TOKEN_CONTEXT_KEY = "authTokenUsed";

const UNAUTHORIZED_STATUS_CODES = [401];
const UNAUTHORIZED_GQL_CODES = ["UNAUTHENTICATED"];

export interface UnauthorizedSignals {
  graphQLErrors?: readonly { extensions?: Record<string, any> | null }[];
  networkError?: unknown;
}

/**
 * What a 401 looks like. Unchanged from the original inline handler — it was correct about *what*,
 * and reads the same three extension shapes the API can serialize a status into.
 */
export function isUnauthorizedResponse({ graphQLErrors, networkError }: UnauthorizedSignals): boolean {
  if (
    networkError &&
    typeof networkError === "object" &&
    "statusCode" in networkError &&
    UNAUTHORIZED_STATUS_CODES.includes((networkError as { statusCode?: unknown }).statusCode as number)
  ) {
    return true;
  }

  for (const err of graphQLErrors ?? []) {
    const ext = err.extensions as Record<string, any> | undefined | null;

    if (!ext) {
      continue;
    }

    const statusCode =
      ext.authError?.statusCode ?? ext.response?.statusCode ?? (typeof ext.code === "number" ? ext.code : undefined);

    if (
      (typeof statusCode === "number" && UNAUTHORIZED_STATUS_CODES.includes(statusCode)) ||
      (typeof ext.code === "string" && UNAUTHORIZED_GQL_CODES.includes(ext.code))
    ) {
      return true;
    }
  }

  return false;
}

export interface SignOutDecision extends UnauthorizedSignals {
  /** The access token the failing operation sent, read back off the operation context. */
  tokenUsed: string | undefined;
  /** The access token in storage at the moment the failure arrived. */
  currentToken: string | undefined;
}

/**
 * Whether a 401 is news.
 *
 * Being 401-only is not enough, because the handler is unconditional in *time*. A request issued
 * before a successful login can 401 after it — which is the normal case on a preview origin, where
 * the token sitting in that origin's `localStorage` is usually stale, so there is almost always a
 * 401 in flight when someone arrives. Acting on it discards the credential that was just minted
 * and, because the consumer's handler does a full-document navigation, cancels every request in
 * flight — a silent bounce to `/login` with no follow-up requests at all, which is exactly the
 * signature measured on the orphaned preview sessions (B-1963, found in review).
 *
 * `tokenUsed !== currentToken` covers every generation mismatch in one comparison, including the
 * case where the failing request carried no token at all and one has since been stored. Equal
 * undefined values still sign out: an unauthenticated request rejected while nothing is stored is
 * a genuine "you are not signed in".
 */
export function shouldSignOut({ currentToken, tokenUsed, ...signals }: SignOutDecision): boolean {
  if (!isUnauthorizedResponse(signals)) {
    return false;
  }

  return tokenUsed === currentToken;
}

/**
 * Attaches the bearer token, and records which token it was.
 *
 * Reads through `getAccessToken` per operation rather than from React state, so the request after
 * a login already carries the fresh token.
 */
export function createAuthLink(getAccessToken: (require?: boolean) => string | undefined): ApolloLink {
  return setContext((_, { headers }) => {
    const token = getAccessToken();

    return {
      [AUTH_TOKEN_CONTEXT_KEY]: token,
      headers: {
        authorization: token ? `Bearer ${token}` : "",
        ...headers,
      },
    };
  });
}

const readTokenUsed = (operation: { getContext: () => Record<string, unknown> }): string | undefined =>
  operation.getContext()[AUTH_TOKEN_CONTEXT_KEY] as string | undefined;

const annotateNetworkError = (networkError: unknown, tokenUsed: string | undefined): unknown => {
  if (networkError && typeof networkError === "object") {
    (networkError as Record<string, unknown>)[AUTH_TOKEN_CONTEXT_KEY] = tokenUsed;
  }

  return networkError;
};

/**
 * Copies rather than mutations: these are server-authored error objects, and the annotation is
 * ours. `ApolloError.graphQLErrors` holds the same element objects this produces, so the extension
 * survives to the provider.
 */
const annotateResultErrors = (result: FetchResult, tokenUsed: string | undefined): FetchResult => ({
  ...result,
  errors: result.errors?.map((graphQLError) => ({
    ...graphQLError,
    extensions: { ...graphQLError.extensions, [AUTH_TOKEN_CONTEXT_KEY]: tokenUsed },
  })),
});

/**
 * Records which credential each failure belongs to, on the failure.
 *
 * Installed unconditionally, because every app's `AccountProvider` needs it — not just the one app
 * that passes `onUnauthorized`. Both transports are annotated, and both matter: a gateway 401
 * arrives as a `networkError`, while the *common* case is an HTTP **200** carrying
 * `errors[].extensions.code === "UNAUTHENTICATED"` (the Apollo driver's serialization of
 * `AppAuthGuard`'s `UnauthorizedException`), which never becomes a `networkError` at all.
 */
export function createAuthTokenAnnotationLink(): ApolloLink {
  return new ApolloLink(
    (operation, forward) =>
      new Observable<FetchResult>((observer) => {
        const subscription = forward(operation).subscribe({
          complete: () => observer.complete(),
          error: (networkError) => observer.error(annotateNetworkError(networkError, readTokenUsed(operation))),
          next: (result) =>
            observer.next(result.errors?.length ? annotateResultErrors(result, readTokenUsed(operation)) : result),
        });

        return () => subscription.unsubscribe();
      })
  );
}

export interface UnauthorizedLinkConfig {
  getAccessToken: (require?: boolean) => string | undefined;
  onUnauthorized: () => void;
}

export function createUnauthorizedLink({ getAccessToken, onUnauthorized }: UnauthorizedLinkConfig): ApolloLink {
  // Still once per client: the handler navigates, and a burst of parallel 401s must not race it.
  let signingOut = false;

  return onError(({ graphQLErrors, networkError, operation }) => {
    if (signingOut) {
      return;
    }

    const tokenUsed = readTokenUsed(operation);

    if (!shouldSignOut({ currentToken: getAccessToken(), graphQLErrors, networkError, tokenUsed })) {
      return;
    }

    signingOut = true;
    onUnauthorized();
  });
}
